import apiClient from '@/lib/apiClient';
import { buildListParams, extractPaginatedData } from '@/shared/utils/queryUtils';

export type RangePreset = 'TODAY' | 'YESTERDAY' | 'LAST_3_DAYS' | 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'CUSTOM';

export type DashboardStats = {
  range: { from: string; to: string; preset: RangePreset | null };
  users: {
    total: number;
    active: number;
    suspended: number;
    underReview: number;
    archived: number;
    newInRange: number;
    newLast24Hours: number;
  };
  roomAds: {
    total: number;
    published: number;
    draft: number;
    rejected: number;
    featured: number;
    newInRange: number;
  };
  wantedAds: { total: number; published: number; newInRange: number };
  reports: {
    pending: number;
    underReview: number;
    resolved: number;
    dismissed: number;
    actionRequired: number;
    newInRange: number;
  };
  admins: { total: number; active: number };
  activity: { inRange: number; recent: AuditLogEntry[] };
  recentActivity: AuditLogEntry[];
  generatedAt: string;
};

export type AuditLogEntry = {
  id: number;
  adminId: number | null;
  adminEmail: string | null;
  action: string;
  targetType: string | null;
  targetId: number | null;
  summary: string | null;
  metadataJson: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
};

export type ReportEntry = {
  id: number;
  reporterId: number | null;
  reporterEmail: string | null;
  reporterName: string | null;
  targetType: 'ROOM_AD' | 'ROOM_WANTED_AD' | 'USER';
  targetId: number;
  reason: string;
  description: string | null;
  status: 'PENDING' | 'UNDER_REVIEW' | 'RESOLVED_ACTION_TAKEN' | 'DISMISSED';
  resolvedByAdminId: number | null;
  resolvedByAdminEmail: string | null;
  resolvedAt: string | null;
  adminNotes: string | null;
  createdAt: string;
};

export const opsApi = {
  stats: async (params?: { preset?: RangePreset; from?: string; to?: string }): Promise<DashboardStats> => {
    const q: Record<string, string> = {};
    if (params?.preset) q.preset = params.preset;
    if (params?.from) q.from = params.from;
    if (params?.to) q.to = params.to;
    const res = await apiClient.get('/admin/dashboard/stats', { params: q });
    return res.data?.data;
  },

  auditLogs: async (params: any) => {
    const queryParams = buildListParams(params);
    const res = await apiClient.get('/admin/audit-logs', { params: queryParams });
    return extractPaginatedData(res);
  },

  auditActions: async (): Promise<string[]> => {
    const res = await apiClient.get('/admin/audit-logs/actions');
    return res.data?.data ?? [];
  },

  reports: async (params: any) => {
    const queryParams = buildListParams(params);
    const res = await apiClient.get('/admin/reports', { params: queryParams });
    return extractPaginatedData(res);
  },

  reportAction: async (id: number, status: ReportEntry['status'], adminNotes?: string) => {
    const res = await apiClient.post(`/admin/reports/${id}/action`, { status, adminNotes });
    return res.data?.data;
  },

  suspendUser: async (id: number, status: 'ACTIVE' | 'SUSPENDED' | 'UNDER_REVIEW', reason?: string) => {
    const res = await apiClient.post(`/admin/users-ops/${id}/status`, { status, reason });
    return res.data?.data;
  },

  bulkUserStatus: async (userIds: number[], status: 'ACTIVE' | 'SUSPENDED' | 'UNDER_REVIEW', reason?: string) => {
    const res = await apiClient.post('/admin/users-ops/bulk-status', { userIds, status, reason });
    return res.data?.data;
  },

  bulkAdStatus: async (adIds: number[], status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'REJECTED', rejectReason?: string) => {
    const res = await apiClient.post('/admin/room-ads-ops/bulk-status', { adIds, status, rejectReason });
    return res.data?.data;
  },

  bulkAdDelete: async (adIds: number[]) => {
    const res = await apiClient.post('/admin/room-ads-ops/bulk-delete', { adIds });
    return res.data?.data;
  },

  bulkAdFlags: async (adIds: number[], flags: Record<string, boolean | undefined>) => {
    const res = await apiClient.post('/admin/room-ads-ops/bulk-flags', { adIds, ...flags });
    return res.data?.data;
  },

  bulkWantedStatus: async (adIds: number[], status: string, rejectReason?: string) => {
    const res = await apiClient.post('/admin/room-wanted-ops/bulk-status', { adIds, status, rejectReason });
    return res.data?.data;
  },

  bulkWantedDelete: async (adIds: number[]) => {
    const res = await apiClient.post('/admin/room-wanted-ops/bulk-delete', { adIds });
    return res.data?.data;
  },

  bulkWantedFlags: async (adIds: number[], flags: Record<string, boolean | undefined>) => {
    const res = await apiClient.post('/admin/room-wanted-ops/bulk-flags', { adIds, ...flags });
    return res.data?.data;
  },
};
